import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, firstValueFrom, race, timer } from 'rxjs';

import { inAppPath } from 'src/app/utils/in-app-path';

/** What the mobile shell fires when a notification tap or a link wants to open a page (cancel it to claim it). */
export const NAVIGATE_EVENT = 'crewdle:navigate';

/** The longest to wait for the app's first navigation before opening the requested page anyway (ms). */
const INITIAL_NAVIGATION_WAIT_MS = 10_000;

interface ShellNavigationApi {
  /** Shell API v5: a request that arrived while this page could not take it yet; returned once. */
  takePendingNavigation?(): string | null;
}

/**
 * Opens a page the mobile shell asks for (a tapped notification, a link) with the app's own router.
 *
 * Until now the shell reloaded the whole web app for every tap. That restarts Firebase, App Check,
 * auth and the access guards, and a tap that arrived early, or on a throttled App Check, landed on
 * the wrong page (the chat home instead of the conversation) or a white one. Here the page is
 * simply navigated to: nothing restarts, and the guards still apply as for any other navigation.
 *
 * Two ways in, both from the shell (../mobile, shell API v5):
 *  - the `crewdle:navigate` event, for a request that arrives while the app is running;
 *  - `crewdleShell.takePendingNavigation()`, for one that arrived while it was still booting
 *    (a cold start): read once, at start.
 * Inert on the web, where neither exists.
 */
@Injectable({ providedIn: 'root' })
export class NativeNavigationService implements OnDestroy {
  private readonly router = inject(Router);
  private started = false;
  private readonly listener = (event: Event): void => this.onNavigateEvent(event as CustomEvent<{ path?: unknown }>);

  /** Last resort if the router itself fails: the shell's old behaviour, a full reload. Replaceable in tests. */
  hardNavigate = (path: string): void => window.location.assign(path);

  /** Listen for the shell's requests, and take the one that may already be waiting. Idempotent. */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    window.addEventListener(NAVIGATE_EVENT, this.listener);

    const shell = (window as unknown as { crewdleShell?: ShellNavigationApi }).crewdleShell;
    const pending = inAppPath(shell?.takePendingNavigation?.());
    if (pending) {
      void this.open(pending);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener(NAVIGATE_EVENT, this.listener);
  }

  private onNavigateEvent(event: CustomEvent<{ path?: unknown }>): void {
    const path = inAppPath(event.detail?.path);
    if (!path) {
      return; // not ours to claim: the shell falls back as it did before
    }
    event.preventDefault(); // claimed: the shell does not reload
    void this.open(path);
  }

  private async open(path: string): Promise<void> {
    await this.initialNavigationSettled();
    try {
      await this.router.navigateByUrl(path);
    } catch (err) {
      console.warn('[native-navigation] the router could not open the page, reloading it', err);
      this.hardNavigate(path);
    }
  }

  /** The app's own first navigation (landing, redirects) must finish first, or it would overwrite this one. */
  private async initialNavigationSettled(): Promise<void> {
    if (this.router.navigated) {
      return;
    }
    const settled = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
    );
    await firstValueFrom(race(settled, timer(INITIAL_NAVIGATION_WAIT_MS)));
  }
}
